/**
 * Editor Toolbar
 *
 * Toolbar component for the page builder with save, preview, properties editing,
 * and other editor controls.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  CircularProgress,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Save as SaveIcon,
  Visibility as PreviewIcon,
  VisibilityOff as EditIcon,
  Edit as MetadataIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Settings as SettingsIcon,
  Share as ShareIcon,
  Download as ExportIcon,
  Public as PublicIcon,
  Lock as PrivateIcon,
  CheckCircle as SavedIcon,
  Drafts as DraftIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import { PageDoc } from '../../types/page-builder.types';
import { ViewCategory } from '../../types/view.types';
import { UnifiedTag } from '../../types/content.types';
import { tagService } from '../../services/content/tag.service';

interface EditorState {
  isSaving: boolean;
  isPreviewMode: boolean;
  hasUnsavedChanges: boolean;
  lastSaved?: Date;
  canUndo: boolean;
  canRedo: boolean;
}

interface ViewMetadata {
  title: string;
  description: string;
  category: ViewCategory;
  tags?: string[];  // Legacy support
  tagIds?: string[];  // Unified tag system
  isPublic: boolean; // Legacy support
  status?: 'draft' | 'private' | 'public' | 'archived'; // Unified status model
  teamId?: string;
  organizationId?: string;
}

interface EditorToolbarProps {
  pageDoc: PageDoc;
  metadata: ViewMetadata;
  editorState: EditorState;
  onSave: () => void;
  onPreviewToggle: () => void;
  onMetadataEdit: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onExport?: () => void;
  onShare?: () => void;
  readonly?: boolean;
  showBreadcrumbs?: boolean;
  organizationName?: string;
  teamName?: string;
}

export const EditorToolbar: React.FC<EditorToolbarProps> = ({
  pageDoc,
  metadata,
  editorState,
  onSave,
  onPreviewToggle,
  onMetadataEdit,
  onUndo,
  onRedo,
  onExport,
  onShare,
  readonly = false,
  showBreadcrumbs = true,
  organizationName,
  teamName,
}) => {
  const [tagsMap, setTagsMap] = useState<Map<string, UnifiedTag>>(new Map());

  // Load tags to resolve IDs to names
  useEffect(() => {
    const loadTags = async () => {
      if (!metadata.organizationId || !metadata.teamId || !metadata.tagIds?.length) return;

      try {
        const tags = await tagService.listUnifiedTags({
          organization_id: metadata.organizationId,
          team_id: metadata.teamId,
          scope: 'team',
        });

        const map = new Map<string, UnifiedTag>();
        tags.forEach(tag => map.set(tag.id, tag));
        setTagsMap(map);
      } catch (error) {
        console.error('Error loading tags for toolbar:', error);
      }
    };

    loadTags();
  }, [metadata.organizationId, metadata.teamId, metadata.tagIds]);

  const formatLastSaved = (date?: Date): string => {
    if (!date) return 'Not saved';

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);

    if (diffMinutes < 1) return 'Auto-saved just now';
    if (diffMinutes === 1) return 'Auto-saved 1 minute ago';
    if (diffMinutes < 60) return `Auto-saved ${diffMinutes} minutes ago`;

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours === 1) return 'Auto-saved 1 hour ago';
    if (diffHours < 24) return `Auto-saved ${diffHours} hours ago`;

    return `Auto-saved ${date.toLocaleDateString()}`;
  };

  const getSaveButtonProps = () => {
    if (editorState.isSaving) {
      return {
        startIcon: <CircularProgress size={16} />,
        children: 'Auto-saving...',
        disabled: true,
        variant: 'outlined' as const,
      };
    }
    
    if (editorState.hasUnsavedChanges) {
      return {
        startIcon: <SaveIcon />,
        children: 'Save Now',
        color: 'primary' as const,
        variant: 'contained' as const,
      };
    }
    
    return {
      startIcon: <SavedIcon />,
      children: 'Auto-saved',
      color: 'success' as const,
      variant: 'outlined' as const,
      disabled: true,
    };
  };

  return (
    <Paper 
      elevation={1} 
      sx={{ 
        borderRadius: 0, 
        borderBottom: 1, 
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ px: 3, py: 2 }}>
        {/* Header Row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          {/* Left Side - Title and Breadcrumbs */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, flex: 1 }}>
            {showBreadcrumbs && (organizationName || teamName) && (
              <Breadcrumbs separator="›" sx={{ typography: 'caption' }}>
                {organizationName && (
                  <Link underline="hover" color="text.secondary">
                    {organizationName}
                  </Link>
                )}
                {teamName && (
                  <Link underline="hover" color="text.secondary">
                    {teamName}
                  </Link>
                )}
                <Typography variant="caption" color="text.primary">
                  {pageDoc.title || 'Untitled View'}
                </Typography>
              </Breadcrumbs>
            )}
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h5" noWrap sx={{ fontWeight: 'medium' }}>
                {pageDoc.title || 'Untitled View'}
              </Typography>
              
              {/* Status Indicators */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Status Chip - use status field if available, fallback to isPublic */}
                {(() => {
                  const status = metadata.status || (metadata.isPublic ? 'public' : 'private');
                  const statusConfig = {
                    draft: { icon: <DraftIcon />, label: 'Draft', color: 'default' as const },
                    private: { icon: <PrivateIcon />, label: 'Private', color: 'info' as const },
                    public: { icon: <PublicIcon />, label: 'Public', color: 'success' as const },
                    archived: { icon: <ArchiveIcon />, label: 'Archived', color: 'warning' as const },
                  };
                  const config = statusConfig[status] || statusConfig.private;

                  return (
                    <Chip
                      icon={config.icon}
                      label={config.label}
                      size="small"
                      variant="outlined"
                      color={config.color}
                    />
                  );
                })()}

                {editorState.hasUnsavedChanges && (
                  <Chip
                    icon={<DraftIcon />}
                    label="Unsaved"
                    size="small"
                    color="warning"
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* Right Side - Action Buttons */}
          {!readonly && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* History Controls */}
              {(onUndo || onRedo) && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title="Undo">
                    <span>
                      <IconButton 
                        onClick={onUndo} 
                        disabled={!editorState.canUndo}
                        size="small"
                      >
                        <UndoIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Redo">
                    <span>
                      <IconButton 
                        onClick={onRedo} 
                        disabled={!editorState.canRedo}
                        size="small"
                      >
                        <RedoIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 1 }} />
                </Box>
              )}

              {/* Preview Toggle */}
              <Tooltip title={editorState.isPreviewMode ? 'Exit Preview' : 'Preview'}>
                <Button
                  onClick={onPreviewToggle}
                  startIcon={editorState.isPreviewMode ? <EditIcon /> : <PreviewIcon />}
                  variant={editorState.isPreviewMode ? 'contained' : 'outlined'}
                  size="small"
                >
                  {editorState.isPreviewMode ? 'Edit' : 'Preview'}
                </Button>
              </Tooltip>

              {/* Properties Edit */}
              <Tooltip title="Edit Properties">
                <Button
                  onClick={onMetadataEdit}
                  startIcon={<MetadataIcon />}
                  variant="outlined"
                  size="small"
                >
                  Properties
                </Button>
              </Tooltip>

              <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 1 }} />

              {/* Secondary Actions */}
              {onShare && (
                <Tooltip title="Share">
                  <IconButton onClick={onShare} size="small">
                    <ShareIcon />
                  </IconButton>
                </Tooltip>
              )}

              {onExport && (
                <Tooltip title="Export">
                  <IconButton onClick={onExport} size="small">
                    <ExportIcon />
                  </IconButton>
                </Tooltip>
              )}

              {/* Save Button */}
              <Button
                onClick={onSave}
                disabled={editorState.isSaving || !editorState.hasUnsavedChanges}
                {...getSaveButtonProps()}
                size="small"
              />
            </Box>
          )}
        </Box>

        {/* Bottom Row - Properties and Last Saved */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'between', gap: 2 }}>
          {/* Description */}
          {pageDoc.description && (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              noWrap 
              sx={{ flex: 1, minWidth: 0 }}
            >
              {pageDoc.description}
            </Typography>
          )}
          
          {/* Tags */}
          {metadata.tagIds && metadata.tagIds.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              {metadata.tagIds.slice(0, 3).map(tagId => {
                const tag = tagsMap.get(tagId);
                return (
                  <Chip
                    key={tagId}
                    label={tag?.name || tagId}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: '0.75rem',
                      height: 20,
                      borderColor: tag?.color || undefined,
                      color: tag?.color || undefined,
                    }}
                  />
                );
              })}
              {metadata.tagIds.length > 3 && (
                <Chip
                  label={`+${metadata.tagIds.length - 3}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem', height: 20 }}
                />
              )}
            </Box>
          )}
          
          {/* Last Saved */}
          {!readonly && (
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              {formatLastSaved(editorState.lastSaved)}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default EditorToolbar;